import React, { Suspense, lazy, useEffect, useState } from 'react';
import { fetchBottleTwin, pushTwinSensor } from '../../lib/api';

// Lazy-load the WebGL scene so the rest of the app stays light.
const BottleScene = lazy(() => import('./BottleScene'));

const Fallback = () => (
  <div
    data-testid="twin-fallback"
    className="w-full h-[420px] flex items-center justify-center"
  >
    <div className="text-center">
      <div className="w-12 h-12 mx-auto rounded-full border-2 border-[#C1D7E2] border-t-[#7C9CB1] animate-spin" />
      <p className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
        Loading bottle…
      </p>
    </div>
  </div>
);

/**
 * Public entry for the Digital Twin module.
 * Owns the API state (bottle level via /twins/:id), renders the WebGL scene,
 * and exposes the Simulate Dispense / Refill controls.
 */
const BottleTwin = ({ bottleId = 'SH-042-0188', label = 'Ananta Radiance Serum' }) => {
  const [twin, setTwin] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetchBottleTwin(bottleId);
      setTwin(res);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottleId]);

  const push = async (deltaMl) => {
    if (!twin || busy) return;
    const nextAfter = Math.max(
      0,
      Math.min(twin.ml_capacity, twin.ml_current + deltaMl)
    );
    setBusy(true);
    try {
      const res = await pushTwinSensor(bottleId, {
        ml_before: twin.ml_current,
        ml_after: nextAfter,
        bottle_capacity_ml: twin.ml_capacity,
      });
      setTwin((prev) => ({
        ...prev,
        ml_current: res.ml_current,
        level: res.ml_capacity ? res.ml_current / res.ml_capacity : 0,
      }));
    } finally {
      setBusy(false);
    }
  };

  const level = twin?.level ?? 0;
  const pct = Math.round(level * 100);

  return (
    <div data-testid="bottle-twin" className="w-full">
      {/* Stage */}
      <div
        className="relative mx-auto"
        style={{
          height: 'min(520px, 70vh)',
          background:
            'radial-gradient(60% 50% at 50% 35%, rgba(193,215,226,0.55) 0%, rgba(193,215,226,0) 65%), radial-gradient(40% 40% at 50% 75%, rgba(124,156,177,0.18) 0%, rgba(124,156,177,0) 70%)',
          borderRadius: '12px',
        }}
      >
        <Suspense fallback={<Fallback />}>
          <BottleScene targetLevel={level} />
        </Suspense>
      </div>

      {/* Readout */}
      <div className="mt-6 text-center">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
          Digital Twin · Bottle {bottleId}
        </p>
        <p className="mt-1 font-serif text-xl text-[#7C9CB1]">{label}</p>
        <p
          data-testid="twin-level"
          className="mt-3 font-serif text-5xl md:text-6xl text-[#7C9CB1] font-light leading-none transition-[font-size] duration-300"
        >
          {pct}%
        </p>
        <p className="mt-2 font-mono text-[11px] tracking-[0.2em] text-[#90B0BF]">
          {twin ? `${Math.round(twin.ml_current)} / ${twin.ml_capacity} ml` : '—'}
        </p>
      </div>

      {/* Controls */}
      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          onClick={() => push(-12)}
          disabled={busy}
          data-testid="twin-dispense"
          className="font-mono text-[10px] tracking-[0.22em] uppercase px-4 py-2 border border-[#C1D7E2] text-[#7C9CB1] hover:bg-[#C1D7E2]/40 transition-colors disabled:opacity-50"
        >
          Simulate dispense
        </button>
        <button
          onClick={() => push(80)}
          disabled={busy}
          data-testid="twin-refill"
          className="font-mono text-[10px] tracking-[0.22em] uppercase px-4 py-2 bg-[#7C9CB1] text-[#F4F8F9] hover:bg-[#90B0BF] transition-colors disabled:opacity-50"
        >
          Simulate refill
        </button>
      </div>
    </div>
  );
};

export default BottleTwin;
